import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
  useCallback,
} from "react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/context/AuthContext";
import { notficationApi } from "@/services/notificationService";
import { UserNotification } from "@/dto/get-notification.dto";

export interface Notification {
  id: string;
  title: string;
  message: string;
  type: "info" | "success" | "warning" | "error";
  read: boolean;
  timestamp: Date;
  link?: string;
}

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  deleteNotification: (id: string) => void;
  clearAllNotifications: () => void;
  fetchingNotfications: boolean;
}

const NotificationContext = createContext<NotificationContextType | undefined>(
  undefined
);

export const NotificationProvider = ({ children }: { children: ReactNode }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [fetchingNotfications, setFetchingNotfications] = useState(false);
  const { user } = useAuth();
  const { toast } = useToast();
  const unreadCount = notifications.filter((n) => !n.read).length;

  const generateNotificationLink = useCallback((
    n: UserNotification
  ): string | undefined => {
    const { variant, meta } = n.notification;

    switch (variant) {
      case "WELCOME":
        return undefined;

      case "NEW_USER":
        return `/users?highlightedUser=${meta.userId}`;

      case "NEW_PROJECT":
        return `/projects?highlightedProject=${meta.projectId}`;

      case "PROJECT_STATUS_CHANGED":
        return meta.projectId ? `/projects/${meta.projectId}?tab=timeline` : undefined;

      case "PROJECT_UPDATED":
        return meta.projectId ? `/projects/${meta.projectId}?tab=timeline` : undefined;
        
      case "NEW_COMMENT":
        return meta.projectId && meta.commentId
          ? `/projects/${meta.projectId}?tab=timeline&commentId=${meta.commentId}`
          : meta.projectId
          ? `/projects/${meta.projectId}?tab=timeline`
          : undefined;

      case "NEW_REPLY":
        return meta.projectId && meta.commentId
          ? `/projects/${meta.projectId}?tab=timeline&commentId=${meta.commentId}`
          : meta.projectId
          ? `/projects/${meta.projectId}?tab=timeline`
          : undefined;

      case "MEMBER_ADDED":
        return meta.projectId ? `/projects/${meta.projectId}?tab=timeline` : undefined;

      case "MEMBER_REMOVED":
      case "MEMBER_LEFT":
        return undefined;

      case "USER_INVITED":
        return "/login";

      case "SUBMISSION_CREATED":
      case "SUBMISSION_UPDATED":
        return meta.projectId 
          ? `/projects/${meta.projectId}?tab=submissions`
          : undefined;

      case "ADDITIONAL_DOC_ADDED":
        return meta.projectId
          ? `/projects/${meta.projectId}?tab=documents`
          : undefined;

      case "MEMBER_JOINED":
        return meta.projectId
          ? `/projects/${meta.projectId}?tab=team`
          : undefined;

      case "PROJECT_ASSIGNMENT":
      case "PROJECT_MANAGER_ASSIGNED":
      case "PROJECT_MANAGER_REMOVED":
      case "AHJ_ASSIGNED":
      case "AHJ_REMOVED":
      case "REVIEW_DATE_CHANGED":
      case "PERMIT_NUMBER_ADDED":
      case "PROJECT_FIELD_UPDATED":
      case "AHJ_REVIEW_DATE_REMINDER":
        return meta.projectId
          ? `/projects/${meta.projectId}?tab=timeline`
          : undefined;

      case "SUBMISSION_RESTORED":
      case "SUBMISSION_FIELD_UPDATED":
        return meta.projectId
          ? `/projects/${meta.projectId}?tab=submissions`
          : undefined;

      case "CREDENTIALS_ADDED":
      case "CREDENTIALS_UPDATED":
        return meta.projectId
          ? `/projects/${meta.projectId}?tab=credentials`
          : undefined;

      default:
        return undefined;
    }
  }, []);

  const mapToFrontendNotification = useCallback((n: UserNotification): Notification => ({
    id: n.id,
    title: n.notification.title,
    message: n.notification.message,
    type: n.notification.type,
    read: n.read,
    timestamp: new Date(n.deliveredAt),
    link: generateNotificationLink(n),
  }), [generateNotificationLink]);

  const fetchNotifications = useCallback(async () => {
    if (!user) return;

    try {
      setFetchingNotfications(true);
      const response = await notficationApi.getNotifications();
      setNotifications(response.notifications.map(mapToFrontendNotification));
    } catch (error) {
      toast({
        title: "Failed to load notifications",
        description: "Please try again later",
        variant: "destructive",
      });
    } finally {
      setFetchingNotfications(false);
    }
  }, [user, toast, mapToFrontendNotification]);

  useEffect(() => {
    if (user) {
      fetchNotifications();
      const interval = setInterval(fetchNotifications, 30000);
      return () => clearInterval(interval);
    } else {
      setNotifications([]);
    }
  }, [user, fetchNotifications]);

  const markAsRead = async (id: string) => {
    try {
      await notficationApi.updateNotification(id, true);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, read: true } : n))
      );
    } catch (error) {
      toast({
        title: "Update failed",
        description: "Could not mark notification as read",
        variant: "destructive",
      });
    }
  };

  const markAllAsRead = async () => {
    try {
      await notficationApi.updateNotification(undefined, true);
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    } catch (error) {
      toast({
        title: "Update failed",
        description: "Could not mark all as read",
        variant: "destructive",
      });
    }
  };

  const deleteNotification = async (id: string) => {
    try {
      await notficationApi.deleteNotification(id);
      setNotifications((prev) => prev.filter((n) => n.id !== id));
    } catch (error) {
      toast({
        title: "Delete failed",
        description: "Could not delete notification",
        variant: "destructive",
      });
    }
  };

  const clearAllNotifications = async () => {
    try {
      await notficationApi.clearAllNotifications();
      setNotifications([]);
    } catch (error) {
      toast({
        title: "Clear failed",
        description: "Could not clear all notifications",
        variant: "destructive",
      });
    }
  };

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        markAsRead,
        markAllAsRead,
        deleteNotification,
        clearAllNotifications,
        fetchingNotfications,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error(
      "useNotifications must be used within a NotificationProvider"
    );
  }
  return context;
};
